import { BaseComponent } from "./base_component"
import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { ProjectProperties } from "."
import { numericDataSocket } from "../socket_types"
import { workerPool } from "../../../modelling/workerPool"
import { NumericTileGrid } from "../tile_grid"
import { SlopeAlgorithm, SlopeUnit } from "../../../modelling/worker/slopeOperations"

type TerrainKind = "slope" | "aspect"

type TerrainOutputSpec = {
  key: string
  label: string
  kind: TerrainKind
  algorithm: SlopeAlgorithm
  unit?: SlopeUnit
  unitProperty: string
}

const TERRAIN_OUTPUTS: TerrainOutputSpec[] = [
  { key: "horn_deg", label: "Slope Horn [[degrees]]", kind: "slope", algorithm: "Horn", unit: "degrees", unitProperty: "degrees" },
  { key: "horn_pct", label: "Slope Horn [[%]]", kind: "slope", algorithm: "Horn", unit: "percent", unitProperty: "%" },
  { key: "ritter_deg", label: "Slope Ritter [[degrees]]", kind: "slope", algorithm: "Ritter", unit: "degrees", unitProperty: "degrees" },
  { key: "ritter_pct", label: "Slope Ritter [[%]]", kind: "slope", algorithm: "Ritter", unit: "percent", unitProperty: "%" },
  { key: "horn_aspect", label: "Aspect Horn [[degrees]]", kind: "aspect", algorithm: "Horn", unitProperty: "degrees" },
  { key: "ritter_aspect", label: "Aspect Ritter [[degrees]]", kind: "aspect", algorithm: "Ritter", unitProperty: "degrees" },
]

export class SlopeComponent extends BaseComponent {
  projectProps: ProjectProperties
  cache: Map<string, NumericTileGrid>

  constructor(projectProps: ProjectProperties) {
    super("Slope")
    this.category = "Calculations"
    this.projectProps = projectProps
    this.cache = new Map()
  }

  private getCacheKey(
    elevation: NumericTileGrid,
    kind: TerrainKind,
    algorithm: SlopeAlgorithm,
    unit: string
  ): string {
    const dataHash = elevation.getData().reduce((acc, val) => acc + (isFinite(val) ? val : 0), 0)
    return `${elevation.zoom}_${elevation.x}_${elevation.y}_${elevation.width}_${elevation.height}_${dataHash}_${kind}_${algorithm}_${unit}`
  }

  async builder(node: Node) {
    node.addInput(new Input("input", "Elevation model", numericDataSocket))

    for (const out of TERRAIN_OUTPUTS) {
      node.addOutput(new Output(out.key, out.label, numericDataSocket))
    }
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (editorNode === undefined) return

    const elevation = inputs["input"][0] as NumericTileGrid | undefined

    if (!elevation) {
      editorNode.meta.errorMessage = "Elevation model is required"
      editorNode.update()
      return
    }

    delete editorNode.meta.errorMessage

    await Promise.all(
      TERRAIN_OUTPUTS.map(async spec => {
        if (node.outputs[spec.key].connections.length === 0) return

        const unitKey = spec.kind === "slope" ? (spec.unit ?? "degrees") : "aspect"
        const cacheKey = this.getCacheKey(elevation, spec.kind, spec.algorithm, unitKey)
        if (!this.cache.has(cacheKey)) {
          const out = await workerPool.queue(async worker => {
            if (spec.kind === "aspect") {
              return worker.calculateAspect(elevation, spec.algorithm)
            }
            return worker.calculateSlope(elevation, spec.algorithm, spec.unit)
          })
          out.properties["unit"] = spec.unitProperty
          this.cache.set(cacheKey, out)
        }
        outputs[spec.key] = this.cache.get(cacheKey)
      })
    )

    editorNode.update()
  }
}
